import React from 'react';
import { Box, Heading, Center, Text } from '@chakra-ui/react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import type { BreakdownSlice } from '../../types/link';
import { CHART_FONT, BRAND_COLORS, DATA_LABEL_STYLE, AXIS_LABEL_STYLE, GRID_COLOR } from './chartTheme';

interface Props {
  title: string;
  data: BreakdownSlice[];
}

/**
 * Vertical column chart for a ranked dimension (country / city / device model
 * / OS / OS version). Highcharts to match the xNotify dashboard charts —
 * rounded columns, dotted gridlines, value data-labels on top. Capped to the
 * top 12 so the x-axis stays legible.
 */
export const ColumnChart: React.FC<Props> = ({ title, data }) => {
  const total = data.reduce((s, d) => s + d.value, 0);
  const top = [...data].sort((a, b) => b.value - a.value).slice(0, 12);

  const options: Highcharts.Options = {
    credits: { enabled: false },
    title: { text: undefined },
    colors: BRAND_COLORS,
    chart: {
      type: 'column',
      height: 320, // explicit — HighchartsReact mis-measures the flex container otherwise (drops x-axis + labels)
      backgroundColor: 'transparent',
      style: { fontFamily: CHART_FONT },
      spacing: [12, 6, 4, 6],
    },
    // Single x-group; each category is its own series so it shows in the
    // legend below (with its colour) — matching the donut/area legend style.
    xAxis: {
      categories: [''],
      lineWidth: 0,
      tickWidth: 0,
      labels: { enabled: false },
    },
    yAxis: {
      title: { text: null },
      allowDecimals: false,
      gridLineDashStyle: 'Dot',
      gridLineColor: GRID_COLOR,
      labels: { style: { ...AXIS_LABEL_STYLE } },
    },
    legend: {
      enabled: true,
      align: 'center',
      verticalAlign: 'bottom',
      layout: 'horizontal',
      itemStyle: { fontSize: '12px', fontWeight: '500', color: '#4b5563' },
    },
    tooltip: {
      useHTML: true,
      headerFormat: '',
      pointFormat: '<b>{series.name}</b><br/>{point.y} clicks',
    },
    plotOptions: {
      column: {
        borderRadius: 3,
        borderWidth: 0,
        groupPadding: 0.08,
        pointPadding: 0.02,
        dataLabels: {
          enabled: true,
          style: { ...DATA_LABEL_STYLE },
          formatter(this: { y?: number }) {
            return this.y && this.y > 0 ? String(this.y) : null;
          },
        },
      },
    },
    series: top.map((d, i) => ({
      type: 'column',
      name: d.label,
      data: [d.value],
      color: BRAND_COLORS[i % BRAND_COLORS.length],
    })) as Highcharts.SeriesColumnOptions[],
  };

  return (
    <Box bg="white" borderWidth="1px" rounded="lg" p={4} shadow="xs">
      <Heading size="xs" color="gray.600" mb={3}>
        {title}
      </Heading>
      <Box h="320px">
        {total === 0 ? (
          <Center h="100%">
            <Text color="gray.400" fontSize="sm">
              No data yet
            </Text>
          </Center>
        ) : (
          <HighchartsReact
            highcharts={Highcharts}
            options={options}
            containerProps={{ style: { height: '100%', width: '100%' } }}
          />
        )}
      </Box>
    </Box>
  );
};
